/**
 * Link Dialog Component
 * Dialog for inserting LaTeX hyperlinks
 */

import React, { useState } from 'react'
import { EditorView } from '@codemirror/view'
import { XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { getSelection, wrapInCommand } from '@/utils/latex/insertionHelpers'

interface LinkDialogProps {
  view: EditorView | null
  isOpen: boolean
  onClose: () => void
  theme?: 'light' | 'dark'
}

const LinkDialog: React.FC<LinkDialogProps> = ({ view, isOpen, onClose, theme = 'light' }) => {
  const [url, setUrl] = useState('')
  const [linkText, setLinkText] = useState('')

  React.useEffect(() => {
    if (isOpen && view) {
      // Get selected text for link text
      const selection = getSelection(view)
      if (selection.text) {
        setLinkText(selection.text)
      }
      // Focus URL input
      setTimeout(() => {
        const urlInput = document.getElementById('link-url-input')
        if (urlInput) {
          (urlInput as HTMLInputElement).focus()
        }
      }, 100)
    } else {
      // Reset form when closed
      setUrl('')
      setLinkText('')
    }
  }, [isOpen, view])

  const handleInsert = () => {
    if (!view || !url.trim()) return

    const selection = getSelection(view)
    const text = linkText.trim() || url.trim()
    
    // Insert \href{url}{text}
    const insertText = `\\href{${url.trim()}}{${text}}`
    const cursorPos = selection.from + insertText.length

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: insertText
      },
      selection: {
        anchor: cursorPos
      }
    })

    view.focus()
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleInsert()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  if (!isOpen) return null

  const dialogClass = clsx(
    'fixed inset-0 z-50 flex items-center justify-center',
    theme === 'dark' ? 'bg-black/50' : 'bg-black/30'
  )

  const contentClass = clsx(
    'relative rounded-lg shadow-xl p-6 w-full max-w-md',
    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
  )

  const inputClass = clsx(
    'w-full px-3 py-2 rounded-md border text-sm',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
  )

  const buttonClass = clsx(
    'px-4 py-2 rounded-md text-sm font-medium transition-colors',
    theme === 'dark'
      ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
  )

  const primaryButtonClass = clsx(
    'px-4 py-2 rounded-md text-sm font-medium transition-colors',
    'bg-primary-600 text-white hover:bg-primary-700',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  )

  return (
    <div className={dialogClass} onClick={onClose}>
      <div className={contentClass} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className={clsx(
            'absolute top-4 right-4 p-1 rounded-md',
            theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
          )}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <h3 className={clsx('text-lg font-semibold mb-4', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
          Insert Link
        </h3>

        <div className="space-y-4">
          <div>
            <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
              URL
            </label>
            <input
              id="link-url-input"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="https://example.com"
              className={inputClass}
            />
          </div>

          <div>
            <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
              Link Text (optional)
            </label>
            <input
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Click here"
              className={inputClass}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className={buttonClass}>
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={!url.trim()}
            className={primaryButtonClass}
          >
            Insert Link
          </button>
        </div>

        <p className={clsx('text-xs mt-4', theme === 'dark' ? 'text-gray-400' : 'text-gray-500')}>
          Press Ctrl+Enter to insert
        </p>
      </div>
    </div>
  )
}

export default LinkDialog

